import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Sticker } from "../components/Sticker";
import { Field } from "../components/Field";
import { PlatformMark } from "../components/PlatformMark";
import { money } from "../lib/format";

interface BulkExportResult {
  filename: string | null;
  csv: string;
  count: number;
  rows: Array<{ id: string; sku: string; title: string | null; warnings: string[] }>;
  skipped: Array<{ sku: string; title: string | null; reason: string }>;
}

/** Upload defaults live in the browser — they rarely change per export. */
const DEPOP_PREFS_KEY = "depopExportPrefs";
const VINTED_PREFS_KEY = "vintedExportPrefs";

function loadDepopPrefs() {
  try {
    return {
      location: "", domesticShipping: "", intlShipping: "",
      ...JSON.parse(localStorage.getItem(DEPOP_PREFS_KEY) ?? "{}"),
    } as { location: string; domesticShipping: string; intlShipping: string };
  } catch {
    return { location: "", domesticShipping: "", intlShipping: "" };
  }
}

function loadVintedPrefs() {
  try {
    return {
      packageSize: "Small",
      ...JSON.parse(localStorage.getItem(VINTED_PREFS_KEY) ?? "{}"),
    } as { packageSize: "Small" | "Medium" | "Large" };
  } catch {
    return { packageSize: "Small" as const };
  }
}

function downloadCsv(filename: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface ActiveListing {
  id: string; platform: string; status: string; listPrice: string | null;
  externalUrl: string | null; syncError: string | null;
  itemId: string; sku: string; title: string | null; daysLive: number | null;
}

export function Listings() {
  const [rows, setRows] = useState<ActiveListing[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [prefs, setPrefs] = useState(loadDepopPrefs);
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState<BulkExportResult | null>(null);
  const [vintedPrefs, setVintedPrefs] = useState(loadVintedPrefs);
  const [vintedExporting, setVintedExporting] = useState(false);
  const [vintedExported, setVintedExported] = useState<BulkExportResult | null>(null);

  async function load() {
    try {
      const res = await api.get<{ listings: ActiveListing[] }>("/listings");
      setRows(res.listings);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load your listings");
    }
  }

  useEffect(() => { void load(); }, []);

  async function delist(id: string) {
    setBusy(id);
    try {
      await api.post(`/listings/${id}/delist`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not end that listing");
    } finally {
      setBusy(null);
    }
  }

  async function archive(id: string) {
    setBusy(id);
    try {
      await api.patch(`/listings/${id}`, { archivedAt: new Date().toISOString() });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive that listing");
    } finally {
      setBusy(null);
    }
  }

  async function remove(id: string) {
    if (!window.confirm("Delete this listing record permanently?")) return;
    setBusy(id);
    try {
      await api.del(`/listings/${id}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete that listing");
    } finally {
      setBusy(null);
    }
  }

  function setPref(key: keyof ReturnType<typeof loadDepopPrefs>, value: string) {
    setPrefs((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(DEPOP_PREFS_KEY, JSON.stringify(next));
      return next;
    });
  }

  async function exportDepop() {
    setExporting(true);
    setError(null);
    setExported(null);
    try {
      const res = await api.post<BulkExportResult>("/export/depop", { ...prefs });
      setExported(res);
      if (res.count > 0 && res.filename) downloadCsv(res.filename, res.csv);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not build the Depop export");
    } finally {
      setExporting(false);
    }
  }

  async function exportVinted() {
    setVintedExporting(true);
    setError(null);
    setVintedExported(null);
    try {
      const res = await api.post<BulkExportResult>("/export/vinted", { ...vintedPrefs });
      setVintedExported(res);
      if (res.count > 0 && res.filename) downloadCsv(res.filename, res.csv);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not build the Vinted export");
    } finally {
      setVintedExporting(false);
    }
  }

  const stale = rows.filter((r) => (r.daysLive ?? 0) > 45);
  const flagged = exported?.rows.filter((r) => r.warnings.length > 0) ?? [];
  const vintedFlagged = vintedExported?.rows.filter((r) => r.warnings.length > 0) ?? [];

  return (
    <div className="shell">
      <header className="masthead">
        <span className="wordmark">listed</span>
        <span className="num" style={{ color: "var(--muted)" }}>{rows.length}</span>
      </header>

      {error && <div className="notice">{error}</div>}

      {stale.length > 0 && (
        <div className="notice ok">
          {stale.length} item{stale.length === 1 ? " has" : "s have"} been live over 45 days.
          A price drop usually moves them.
        </div>
      )}

      <section className="rail">
        <h2 className="eyebrow rail-label">Bulk list on Depop</h2>
        <p className="hint">
          Builds a CSV for Depop's bulk lister from every business item that isn't
          on Depop yet. Downloading changes nothing here — items stay as they are
          until you record the listing or a sales report ties them out. Photo
          links expire after a few hours, so import it soon after downloading.
        </p>
        <Field label="Location" hint="Shown on every listing, e.g. Orlando, United States">
          <input
            value={prefs.location}
            onChange={(e) => setPref("location", e.target.value)}
            placeholder="Orlando, United States"
          />
        </Field>
        <Field label="Domestic shipping">
          <input
            value={prefs.domesticShipping}
            onChange={(e) => setPref("domesticShipping", e.target.value)}
            placeholder="e.g. 5.95 — blank to set per item on Depop"
          />
        </Field>
        <Field label="International shipping">
          <input
            value={prefs.intlShipping}
            onChange={(e) => setPref("intlShipping", e.target.value)}
            placeholder="blank = no international"
          />
        </Field>
        <button onClick={() => void exportDepop()} disabled={exporting}>
          {exporting ? "Building…" : "Download Depop CSV"}
        </button>

        {exported && exported.count === 0 && (
          <div className="notice">Nothing eligible to export — everything is already on Depop.</div>
        )}
        {exported && exported.count > 0 && (
          <div className="notice ok">
            {exported.count} item{exported.count === 1 ? "" : "s"} exported
            {exported.skipped.length > 0 && <>, {exported.skipped.length} skipped</>}.
          </div>
        )}
        {exported?.skipped.map((s) => (
          <p key={s.sku} className="hint">
            Skipped {s.sku} {s.title ? `(${s.title})` : ""} — {s.reason}
          </p>
        ))}
        {flagged.length > 0 && (
          <div className="notice">
            Check before uploading:
            {flagged.map((r) => (
              <p key={r.id} className="hint">
                <Link to={`/items/${r.id}`}>{r.sku}</Link> — {r.warnings.join("; ")}
              </p>
            ))}
          </div>
        )}
      </section>

      <section className="rail">
        <h2 className="eyebrow rail-label">Bulk list on Vinted</h2>
        <p className="hint">
          Builds a CSV matching the Vinted intake workbook — columns line up
          with its Listings tab, so rows paste straight in. Covers every
          business item that isn't on Vinted yet. Read-only, like the Depop
          export, and the photo links expire after a few hours, so use it the
          same sitting.
        </p>
        <Field label="Package size" hint="Small = large envelope, Medium = shoebox, Large = moving box">
          <select
            value={vintedPrefs.packageSize}
            onChange={(e) => {
              const next = { packageSize: e.target.value as "Small" | "Medium" | "Large" };
              setVintedPrefs(next);
              localStorage.setItem(VINTED_PREFS_KEY, JSON.stringify(next));
            }}
          >
            <option>Small</option>
            <option>Medium</option>
            <option>Large</option>
          </select>
        </Field>
        <button onClick={() => void exportVinted()} disabled={vintedExporting}>
          {vintedExporting ? "Building…" : "Download Vinted CSV"}
        </button>

        {vintedExported && vintedExported.count === 0 && (
          <div className="notice">Nothing eligible to export — everything is already on Vinted.</div>
        )}
        {vintedExported && vintedExported.count > 0 && (
          <div className="notice ok">
            {vintedExported.count} item{vintedExported.count === 1 ? "" : "s"} exported
            {vintedExported.skipped.length > 0 && <>, {vintedExported.skipped.length} skipped</>}.
          </div>
        )}
        {vintedExported?.skipped.map((s) => (
          <p key={s.sku} className="hint">
            Skipped {s.sku} {s.title ? `(${s.title})` : ""} — {s.reason}
          </p>
        ))}
        {vintedFlagged.length > 0 && (
          <div className="notice">
            Check before listing (also noted in each row's notes_to_claude):
            {vintedFlagged.map((r) => (
              <p key={r.id} className="hint">
                <Link to={`/items/${r.id}`}>{r.sku}</Link> — {r.warnings.join("; ")}
              </p>
            ))}
          </div>
        )}
      </section>

      <section className="rail">
        <h2 className="eyebrow rail-label">Everything you've listed</h2>

        {rows.length === 0 ? (
          <div className="empty">
            <p>Nothing listed yet.</p>
            <Link to="/"><button className="ghost">Go list something</button></Link>
          </div>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="tag" style={{ cursor: "default" }}>
              <PlatformMark platform={r.platform} />
              <span className="tag-body">
                <span className="tag-title">
                  <Link to={`/items/${r.itemId}`}>{r.title ?? r.sku}</Link>
                </span>
                <span className="tag-meta">
                  <Sticker status={r.status} />
                  <span className="num">{r.sku}</span>
                  <span>{r.platform}</span>
                  {r.daysLive !== null && <span className="num">{r.daysLive}d listed</span>}
                </span>
              </span>
              <span className="tag-price">
                <span className="ask">{money(r.listPrice)}</span>
                <span className="row-actions">
                  <button
                    className="quiet"
                    onClick={() => void delist(r.id)}
                    disabled={busy === r.id}
                  >
                    {busy === r.id ? "…" : "End"}
                  </button>
                  <button
                    className="quiet"
                    onClick={() => void archive(r.id)}
                    disabled={busy === r.id}
                  >
                    Archive
                  </button>
                  <button
                    className="quiet danger"
                    onClick={() => void remove(r.id)}
                    disabled={busy === r.id}
                  >
                    Delete
                  </button>
                </span>
              </span>
            </div>
          ))
        )}
      </section>
    </div>
  );
}
